import type { Application, FederatedPointerEvent } from 'pixi.js-legacy';
import type { GridRenderer } from './grid-renderer';
import type { GameState } from '../game/game-state';
import { findGroup } from '../game/board';
import { SoundManager } from '../game/sound-manager';

/**
 * Handle user input (mouse and touch)
 */
export class InputHandler {
  private app: Application;
  private gridRenderer: GridRenderer;
  private gameState: GameState;
  private onMove: (
    removedTiles: Array<{ x: number; y: number }> | null
  ) => void;

  constructor(
    app: Application,
    gridRenderer: GridRenderer,
    gameState: GameState,
    onMove: (removedTiles: Array<{ x: number; y: number }> | null) => void
  ) {
    this.app = app;
    this.gridRenderer = gridRenderer;
    this.gameState = gameState;
    this.onMove = onMove;

    this.setupListeners();
  }

  private setupListeners(): void {
    const stage = this.app.stage;

    // Mouse/touch move for hover effect
    stage.eventMode = 'static';
    stage.on('pointermove', (event: FederatedPointerEvent) =>
      this.handlePointerMove(event)
    );

    // Mouse/touch click/tap
    stage.on('pointerdown', (event: FederatedPointerEvent) =>
      this.handlePointerDown(event)
    );

    // Clear highlight when leaving canvas
    stage.on('pointerout', () => this.gridRenderer.clearHighlight());
  }

  private handlePointerMove(event: FederatedPointerEvent): void {
    // Use local coordinates relative to the stage
    const gridPos = this.gridRenderer.screenToGrid(
      event.globalX,
      event.globalY
    );

    if (gridPos) {
      const board = this.gameState.getBoard();
      const group = findGroup(board.grid, gridPos.x, gridPos.y);
      this.gridRenderer.highlightGroup(group);
    } else {
      this.gridRenderer.clearHighlight();
    }
  }

  private handlePointerDown(event: FederatedPointerEvent): void {
    // Use local coordinates relative to the stage
    const x = event.globalX;
    const y = event.globalY;

    const gridPos = this.gridRenderer.screenToGrid(x, y);

    if (gridPos) {
      // Play click sound
      SoundManager.play('click');

      const removedTiles = this.gameState.handleClick(gridPos.x, gridPos.y);
      if (removedTiles) {
        this.onMove(removedTiles);
      }
    }
  }
}
